import React from 'react';
import { Form, Row, Col } from 'react-bootstrap';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { updateMetronomeSettings } from '../store/appSlice';
import { TIME_SIGNATURE_PRESETS, TimeSignature } from '../types';
import DarkSelect from './DarkSelect';

function timeSigKey(ts: TimeSignature): string {
  return `${ts.beatsPerBar}/${ts.beatUnit}|${(ts.accents ?? [1]).join(',')}`;
}

const MetronomeSettings: React.FC = () => {
  const dispatch = useAppDispatch();
  const settings = useAppSelector((state) => state.app.metronome);

  const handleTempoModeChange = (mode: 'fixed' | 'auto') => {
    dispatch(updateMetronomeSettings({ autoSpeedUp: mode === 'auto' }));
  };

  const handleStartBpmChange= (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(updateMetronomeSettings({ startBpm: parseInt(e.target.value, 10) }));
  };

  const handleTargetBpmChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(updateMetronomeSettings({ targetBpm: parseInt(e.target.value, 10) }));
  };

  const handleIncreaseBpmChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(updateMetronomeSettings({ increaseBpm: parseInt(e.target.value, 10) }));
  };

  const handleBeatsBeforeIncreaseChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(updateMetronomeSettings({ beatsBeforeIncrease: parseInt(e.target.value, 10) }));
  };

  const handleTimeSignatureChange = (value: string) => {
    const preset = TIME_SIGNATURE_PRESETS.find((p) => timeSigKey(p.value) === value);
    if (preset) {
      dispatch(updateMetronomeSettings({ timeSignature: preset.value }));
    }
  };

  const currentSigKey = timeSigKey(settings.timeSignature);
  const accents = settings.timeSignature.accents ?? [1];

  return (
    <div className="metronome-settings">
      <h5 className="mb-3">Metronome Settings</h5>

      <Form.Group className="mb-3">
        <Form.Label>Time Signature</Form.Label>
        <DarkSelect
          value={currentSigKey}
          onChange={handleTimeSignatureChange}
          placeholder=""
          options={TIME_SIGNATURE_PRESETS.map((p) => ({
            value: timeSigKey(p.value),
            label: p.label,
          }))}
        />
        <Form.Text className="text-muted">
          {settings.timeSignature.beatsPerBar} beats per bar
          {accents.length > 1
            ? ` — accents on beats ${accents.join(', ')}`
            : ' — accent on beat 1'}
          . BPM is per {settings.timeSignature.beatUnit === 8 ? 'eighth note' : settings.timeSignature.beatUnit === 2 ? 'half note' : 'quarter note'}.
        </Form.Text>
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Check
          inline
          type="radio"
          id="metronome-tempo-mode-fixed"
          name="metronomeTempoMode"
          label="Fixed BPM"
          checked={!settings.autoSpeedUp}
          onChange={() => handleTempoModeChange('fixed')}
        />
        <Form.Check
          inline
          type="radio"
          id="metronome-tempo-mode-auto"
          name="metronomeTempoMode"
          label="Auto Speed Up"
          checked={settings.autoSpeedUp}
          onChange={() => handleTempoModeChange('auto')}
        />
      </Form.Group>

      {settings.autoSpeedUp && (
        <>
          <Form.Group className="mb-3">
            <Form.Text className="text-muted">
              Gradually increases tempo after each loop until target BPM is reached.
            </Form.Text>
          </Form.Group>
          <Row className="mb-3">
            <Col>
              <Form.Group>
                <Form.Label>Start BPM</Form.Label>
                <Form.Control
                  type="number"
                  value={settings.startBpm}
                  onChange={handleStartBpmChange}
                  min={20}
                  max={300}
                />
              </Form.Group>
            </Col>
            <Col>
              <Form.Group>
                <Form.Label>Target BPM</Form.Label>
                <Form.Control
                  type="number"
                  value={settings.targetBpm}
                  onChange={handleTargetBpmChange}
                  min={20}
                  max={300}
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Increase by BPM</Form.Label>
            <Form.Control
              type="number"
              value={settings.increaseBpm}
              onChange={handleIncreaseBpmChange}
              min={1}
              max={50}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Beats Before Increase</Form.Label>
            <Form.Control
              type="number"
              value={settings.beatsBeforeIncrease}
              onChange={handleBeatsBeforeIncreaseChange}
              min={1}
              max={100}
            />
          </Form.Group>
        </>
      )}
    </div>
  );
};

export default MetronomeSettings;
